import './style.modules.scss'

import {
  Button,
  ButtonVariant,
  MaterialIcon,
  MaterialIconSize,
  SidePanelContainer,
  useSetBodyScroll
} from '@ampcore/adel-react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import React, { MouseEvent, useState } from 'react'
import xss from 'xss'

export type AnchorLinksProps = {
  columns?: string
  backgroundColor?: string
  backgroundContrast?: string
  title?: string
  templateContent: string
  testId?: string
}

export const AnchorLinks: React.FC<AnchorLinksProps> = (
  props: AnchorLinksProps
) => {
  const {
    columns,
    backgroundColor,
    backgroundContrast,
    title,
    templateContent,
    testId
  } = props

  const [isSidePanelVisible, setIsSidePanelVisible] = useState(false)
  const setBodyScrollLock = useSetBodyScroll(false)

  const toggleVisibility = () => {
    setIsSidePanelVisible(prevState => {
      const newState = !prevState
      setBodyScrollLock(newState)
      return newState
    })
  }

  const handleAnchorClick = (
    event: MouseEvent<HTMLElement>,
    toggleMobileSidePanel = false
  ) => {
    const target = event.target as HTMLElement
    setIsSidePanelVisible(false)
    if (target.tagName === 'A') {
      addSmoothScroll(event)
      toggleMobileSidePanel && toggleVisibility()
    }
  }

  const addSmoothScroll = (e: MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLAnchorElement
    const href = target.getAttribute('href')
    const targetId = href?.replace(/^#/, '')
    const targetElement = targetId && document.getElementById(targetId)
    if (targetElement) {
      e.preventDefault()
      targetElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div className={`AnchorLinks ${backgroundColor ?? ''}`}>
      <div
        className={`u-sm-hidden ${backgroundContrast ?? ''} ${
          columns === 'Two Columns' ? 'AnchorLinks-two-columns' : ''
        }`}
        data-testid={testId ? `${testId}-desktop-section` : undefined}
      >
        <Heading title={title} testId={testId} isMobile={false} />

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          onClick={handleAnchorClick}
          className="u-paddingHoriz20 u-paddingBottom20 u-paddingTop8"
          dangerouslySetInnerHTML={{ __html: xss(templateContent) }}
        />
      </div>

      <div
        className={
          'AnchorLinks-mobile-container u-bgColorStone u-flex u-flexAlignItemsCenter u-paddingHoriz20 u-md-hidden u-lg-hidden u-flexJustifyBetween'
        }
      >
        <Heading title={title} testId={testId} isMobile={false} />
        <SidePanelContainer
          triggerNode={
            <Button
              variant={ButtonVariant.TERTIARY}
              className="u-flex u-flexAlignItemsCenter u-padding0"
              onClick={toggleVisibility}
              data-testid={testId ? `${testId}-open-menu` : undefined}
            >
              <MaterialIcon
                size={MaterialIconSize.SIZE_36}
                icon={AmpcoreMaterialIconId.MENU_OPEN}
                ariaLabel="Link"
                className="u-textColorActionBlue"
              />
            </Button>
          }
          isPanelClose={!isSidePanelVisible}
          minimizable={false}
          heading={title?.trim() ? title : 'In this article'}
          handleCustomClose={() => setIsSidePanelVisible(false)}
          testId={testId ? `${testId}-side-panel` : undefined}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={handleAnchorClick}
            dangerouslySetInnerHTML={{ __html: templateContent }}
            className="u-padding16"
          />
        </SidePanelContainer>
      </div>
    </div>
  )
}

type HeadingProps = {
  title?: string
  isSidePanelVisible?: boolean
  isMobile: boolean
  testId?: string
}

const Heading: React.FC<HeadingProps> = ({
  title,
  isSidePanelVisible,
  isMobile,
  testId
}) => (
  <div
    className={`${
      !isMobile &&
      'AnchorLinks-heading u-paddingBottom20 u-paddingTop32 u-paddingHoriz32 u-marginBottom0'
    }`}
  >
    <h4
      className={`u-textPrimary u-textNormal ${
        isSidePanelVisible
          ? 'u-textSizeMsrp4 u-textColorNeutralGrey'
          : 'u-textSizeMsrp2'
      }`}
      data-testid={testId ? `${testId}-heading` : undefined}
    >
      {title?.trim() ? title : 'In this article'}
      <MaterialIcon
        icon={AmpcoreMaterialIconId.LINK}
        className="u-colorBrandIcon u-sm-hidden u-marginLeft8 u-marginBottomM4"
      />
    </h4>
  </div>
)
